/**
 * T209 — ErechnungMismatchWarning (FR-012b).
 *
 * Renders on the expense detail page when the eRechnung pipeline detected
 * that the embedded XML `grossTotal` disagrees with the value extracted
 * from the PDF text layer. Shows both numbers + the delta percent so the
 * user can reconcile manually before escalating to `neu`.
 *
 * The component is render-only: the decision to show it lives on the
 * `ExpenseDetailDto.erechnungAmountMismatch` field. Backend population is
 * deferred to the expense_processing_status refactor (Phase-11 follow-up).
 */
import { formatCurrency, formatNumber } from '@armard/shared';
import type { ExpenseDetailDto } from '@armard/shared';

export interface ErechnungMismatchWarningProps {
  mismatch: NonNullable<ExpenseDetailDto['erechnungAmountMismatch']>;
}

export function ErechnungMismatchWarning({ mismatch }: ErechnungMismatchWarningProps) {
  return (
    <aside
      className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm"
      data-testid="erechnung-mismatch-warning"
      role="alert"
    >
      <header className="flex items-center gap-2 text-amber-200 font-medium">
        <span aria-hidden className="text-base">⚠</span>
        <span>eRechnung-Abweichung: XML vs. PDF-Text</span>
      </header>
      <p className="mt-1 text-amber-100/80">
        Die in der eingebetteten XML hinterlegte Bruttosumme stimmt nicht mit
        dem aus dem PDF-Text extrahierten Betrag überein. Bitte prüfe den
        Beleg manuell, bevor du ihn auf <code>neu</code> setzt (FR-012b).
      </p>
      <dl className="mt-2 grid grid-cols-3 gap-2 text-xs text-amber-100/90 tabular-nums">
        <div>
          <dt className="text-amber-200/70">XML-Brutto</dt>
          <dd data-testid="erechnung-mismatch-xml">{formatCurrency(Number(mismatch.xmlGross))}</dd>
        </div>
        <div>
          <dt className="text-amber-200/70">PDF-Text-Brutto</dt>
          <dd data-testid="erechnung-mismatch-pdf">{formatCurrency(Number(mismatch.pdfTextGross))}</dd>
        </div>
        <div>
          <dt className="text-amber-200/70">Abweichung</dt>
          <dd data-testid="erechnung-mismatch-delta">
            {formatNumber(mismatch.deltaPercent)} %
          </dd>
        </div>
      </dl>
    </aside>
  );
}
